import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useRouter } from 'next/router'

const DeviceInfoContext = createContext(undefined)

export const useDeviceInfo = () => {
  const context = useContext(DeviceInfoContext)
  if (!context) {
    throw new Error('useDeviceInfo must be used within a DeviceInfoProvider')
  }
  return context
}

export const DeviceInfoProvider = ({ children, userId }) => {
  const [deviceInfo, setDeviceInfo] = useState({
    windowSize: { width: 0, height: 0 },
    userAgent: '',
    deviceType: '',
    language: '',
    timeZone: '',
    screenSize: { width: 0, height: 0 },
    colorDepth: 0,
    isOnline: true,
    isBot: false,
    cookiesEnabled: false,
    url: '',
    referrer: '',
    platform: '',
    vendor: '',
    maxTouchPoints: 0,
    pixelRatio: 1,
    orientation: '',
    hardwareConcurrency: 0,
  })
  const router = useRouter()

  const detectBot = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      return false
    }

    if (userId) {
      return false
    }

    const botPatterns = [
      'bot',
      'crawler',
      'spider',
      'crawling',
      'googlebot',
      'baiduspider',
      'bingbot',
      'yandexbot',
      'yahoo',
      'slurp',
      'duckduckbot',
      'facebookexternalhit',
      'twitterbot',
      'rogerbot',
      'linkedinbot',
      'embedly',
      'quora link preview',
      'showyoubot',
      'outbrain',
      'pinterest',
      'slackbot',
      'vkShare',
      'W3C_Validator',
    ]

    const ua = navigator.userAgent.toLowerCase()
    const isLikelyBot = botPatterns.some((pattern) => ua.includes(pattern))
    const hasWebDriver = 'webdriver' in navigator
    const hasLanguages = 'languages' in navigator
    const hasWebGL = !!window.WebGLRenderingContext
    const isLikelyHuman = hasLanguages && hasWebGL && !hasWebDriver

    return isLikelyBot || !isLikelyHuman
  }, [userId])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const updateDeviceInfo = () => {
      setDeviceInfo((prevInfo) => ({
        ...prevInfo,
        windowSize: { width: window.innerWidth, height: window.innerHeight },
        userAgent: window.navigator.userAgent,
        deviceType: getDeviceType(),
        language: window.navigator.language,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenSize: {
          width: window.screen.width,
          height: window.screen.height,
        },
        colorDepth: window.screen.colorDepth,
        isBot: detectBot(),
        isOnline: navigator.onLine,
        cookiesEnabled: window.navigator.cookieEnabled,
        url: window.location.href,
        referrer: document.referrer,
        platform: window.navigator.platform,
        vendor: window.navigator.vendor,
        maxTouchPoints: window.navigator.maxTouchPoints || 0,
        pixelRatio: window.devicePixelRatio || 1,
        orientation: window.screen.orientation?.type || '',
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
      }))
    }

    const getDeviceType = () => {
      const ua = navigator.userAgent
      if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        return 'tablet'
      }
      if (
        /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
          ua
        )
      ) {
        return 'mobile'
      }
      return 'desktop'
    }

    updateDeviceInfo()

    window.addEventListener('resize', updateDeviceInfo)
    window.addEventListener('online', updateDeviceInfo)
    window.addEventListener('offline', updateDeviceInfo)

    return () => {
      window.removeEventListener('resize', updateDeviceInfo)
      window.removeEventListener('online', updateDeviceInfo)
      window.removeEventListener('offline', updateDeviceInfo)
    }
  }, [detectBot])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleRouteChangeComplete = (url) => {
      setDeviceInfo((prevInfo) => ({
        ...prevInfo,
        url,
        referrer: document.referrer,
      }))
    }

    router.events.on('routeChangeComplete', handleRouteChangeComplete)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
    }
  }, [router])

  const contextValue = useMemo(
    () => ({
      deviceInfo,
    }),
    [deviceInfo]
  )

  return (
    <DeviceInfoContext.Provider value={contextValue}>
      {children}
    </DeviceInfoContext.Provider>
  )
}
